import { useCallback, useEffect, useRef, useState } from "react";
import useFileUploadStore from "../../../../stores/fileUploadStore";
import { usePollBatchDetail } from "../../../../hooks/queries/useBulkUploadsQueries";
import { extractAxiosErrorMessage } from "../../../../services/utils/errorUtils";
import { POLL_TIMEOUT_MS, TERMINAL_BATCH_STATUSES } from "../fileUploadConfig";

export const TIMEOUT_FALLBACK_MESSAGE =
  "Validation is taking longer than expected";

export function useBulkValidationPoll() {
  const disbursementId = useFileUploadStore((s) => s.disbursementId);
  const uiState = useFileUploadStore((s) => s.uiState);
  const isPolling = uiState === "validating";

  const [isTimedOut, setIsTimedOut] = useState(false);
  const [timeoutMessage, setTimeoutMessage] = useState(
    TIMEOUT_FALLBACK_MESSAGE,
  );
  // Captured the moment a terminal status is detected, but not yet applied to
  // the store — Step2Upload finishes its progress animation first, then calls
  // commitResult() so the screen swap lines up with the bar hitting 100%.
  const [pendingResult, setPendingResult] = useState(null);
  const timeoutRef = useRef(null);

  const { data, error } = usePollBatchDetail(disbursementId, {
    enabled: isPolling && !!disbursementId && !isTimedOut && !pendingResult,
  });

  const pollDataRef = useRef(data);
  pollDataRef.current = data;

  const pollErrorRef = useRef(error);
  pollErrorRef.current = error;

  const pendingResultRef = useRef(pendingResult);
  pendingResultRef.current = pendingResult;

  const status = data?.status;

  // Capture a terminal result once — commitResult() applies it later
  useEffect(() => {
    if (!isPolling || !status || pendingResult) return;
    if (!TERMINAL_BATCH_STATUSES.has(status)) return;

    clearTimeout(timeoutRef.current);

    const { paymentChannel, recipientCount, batchId } = pollDataRef.current;
    setPendingResult({ status, paymentChannel, recipientCount, batchId });
  }, [isPolling, status, pendingResult]);

  const commitResult = useCallback(() => {
    const result = pendingResultRef.current;
    if (!result) return;
    const {
      status: resultStatus,
      paymentChannel,
      recipientCount,
      batchId,
    } = result;
    const store = useFileUploadStore.getState();
    if (resultStatus === "PENDING_VALIDATION_ERRORS") {
      store.setValidationErrors({ paymentChannel, recipientCount, batchId });
    } else if (resultStatus === "FILE_UPLOAD_FAILED") {
      store.setFileUploadFailed();
    } else {
      store.setReadyForApproval({ paymentChannel, recipientCount, batchId });
    }
  }, []);

  // Hard timeout — stop polling and let the component surface a message
  useEffect(() => {
    if (!isPolling || isTimedOut || pendingResult) {
      clearTimeout(timeoutRef.current);
      return;
    }
    timeoutRef.current = setTimeout(() => {
      const lastError = pollErrorRef.current;
      setTimeoutMessage(
        lastError
          ? extractAxiosErrorMessage(lastError, TIMEOUT_FALLBACK_MESSAGE)
          : TIMEOUT_FALLBACK_MESSAGE,
      );
      setIsTimedOut(true);
    }, POLL_TIMEOUT_MS);
    return () => clearTimeout(timeoutRef.current);
  }, [isPolling, isTimedOut, pendingResult]);

  return {
    isPolling,
    isTimedOut,
    timeoutMessage,
    isFinishing: !!pendingResult,
    commitResult,
  };
}
